"use client"

import { useEffect, useState, useMemo } from "react"
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Card,
  CardContent,
  Skeleton,
} from "@mui/material"
import {
  Payment as PaymentIcon,
  Warning,
  Error,
  History as HistoryIcon,
  Visibility as VisibilityIcon,
  CheckCircle,
  Print as PrintIcon,
  Delete as DeleteIcon,
  ChevronLeft,
  ChevronRight,
  RestartAlt as ResetIcon,
} from "@mui/icons-material"
import { useDispatch, useSelector } from "react-redux"
import { deleteSale, fetchCreditSales, payRemainingAmount } from "../../redux/actions/sales"
import { toast } from "react-toastify"
import moment from "moment"
import { fetchAllShops, selectShop } from "../../redux/actions/shop"
import ProductDetailsDialog from "../../components/ProductDetailsDialog"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment"
import { generateReceipt } from "../../utils/receiptGenerator"
import { useInvoiceNumber } from "../../hooks/useInvoiceNumber"
import { useTheme } from "@mui/material/styles"
import { tokens } from "../../theme"
import useSalesBatch from "../../hooks/useSalesBatch"

const ROWS_PER_PAGE = 50

const SkeletonRows = ({ count = 5, columns = 8 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <TableRow key={i}>
        {Array.from({ length: columns }).map((_, j) => (
          <TableCell key={j}><Skeleton variant="text" /></TableCell>
        ))}
      </TableRow>
    ))}
  </>
)

const VenteParCredit = () => {
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.user)
  const { shops, selectedShop } = useSelector((state) => state.shop)
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)

  const [openPayment, setOpenPayment] = useState(false)
  const [selectedSale, setSelectedSale] = useState(null)
  const [paymentData, setPaymentData] = useState({ amount: "", payment_method: "cash" })
  const [openHistory, setOpenHistory] = useState(false)
  const [openProductDetails, setOpenProductDetails] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [searchInput, setSearchInput] = useState("")
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)

  const { getInvoiceNumber } = useInvoiceNumber()

  const electronicShops = shops.filter((shop) => shop.enum === "electronics" || shop.type === "electronics")

  const currentShopId = useMemo(() => {
    if (user?.role === "superadmin" || user?.role === "supervisor") {
      return selectedShop?._id || "all"
    }
    return typeof user?.shop === "object" ? user.shop._id : user?.shop
  }, [user, selectedShop])

  const filters = useMemo(() => ({
    paymentMethod: "debt",
    paymentStatus: "partial,unpaid,paid",
    startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : undefined,
    endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : undefined,
    search: searchQuery || undefined,
  }), [startDate, endDate, searchQuery])

  const { data: creditSales, isLoading, page, setPage, total, totalPages, invalidateCache } = useSalesBatch({
    shopId: currentShopId,
    filters,
    limit: ROWS_PER_PAGE,
  })

  useEffect(() => {
    if (user?.role === "superadmin" || user?.role === "supervisor") {
      dispatch(fetchAllShops())
    }
  }, [dispatch, user])

  const handleShopChange = (event) => {
    const shopId = event.target.value
    if (shopId === "all") {
      dispatch(selectShop(null))
    } else {
      const shop = shops.find((s) => s._id === shopId)
      dispatch(selectShop(shop))
    }
  }

  const handleSearchSubmit = () => setSearchQuery(searchInput)
  const handleSearchKeyDown = (e) => { if (e.key === "Enter") handleSearchSubmit() }

  const handleResetFilters = () => {
    setSearchQuery("")
    setSearchInput("")
    setStartDate(null)
    setEndDate(null)
  }

  const handlePayment = async () => {
    try {
      if (!paymentData.amount || paymentData.amount <= 0) {
        toast.error("Please enter a valid amount")
        return
      }

      if (paymentData.amount > selectedSale.remaining_amount) {
        toast.error("Payment amount cannot exceed remaining amount")
        return
      }

      const shopId =
        user?.role === "superadmin" ? selectedShop?._id : typeof user?.shop === "object" ? user.shop._id : user?.shop

      if (!shopId) {
        toast.error("Shop information is missing")
        return
      }

      await dispatch(
        payRemainingAmount(selectedSale._id, {
          ...paymentData,
          shop_id: shopId,
          user: user._id,
        })
      )

      toast.success("Paiement traité avec succès")
      setOpenPayment(false)
      setPaymentData({ amount: 0, payment_method: "cash" })
      setSelectedSale(null)
      invalidateCache()
      setPage(1)
    } catch (error) {
      toast.error(error.message || "Failed to process payment")
    }
  }

  const getStatusChip = (status) => {
    switch (status) {
      case "partial":
        return <Chip icon={<Warning />} label="Partial" color="warning" size="small" />
      case "unpaid":
        return <Chip icon={<Error />} label="Unpaid" color="error" size="small" />
      case "paid":
        return <Chip icon={<CheckCircle />} label="Paid" color="success" size="small" />
      default:
        return null
    }
  }

  const handleDelete = async (saleId) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette vente ?")) {
      try {
        await dispatch(deleteSale(saleId))
        toast.success("Vente supprimée avec succès")
        invalidateCache()
        setPage(1)
      } catch (error) {
        toast.error(error.message || "Erreur lors de la suppression")
      }
    }
  }

  const handlePrint = async (sale) => {
    try {
      const shopId = sale.shop?._id || sale.shop
      const invoiceNumber = await getInvoiceNumber(shopId)
      const doc = await generateReceipt(sale, invoiceNumber)
      doc.save(`facture-${invoiceNumber}.pdf`)
    } catch (error) {
      toast.error("Erreur lors de l'impression de la facture")
      console.error("Print error:", error)
    }
  }

  useEffect(() => {
    return () => {
      setSelectedSale(null)
      setPaymentData({ amount: 0, payment_method: "cash" })
      setOpenPayment(false)
      setOpenHistory(false)
      setOpenProductDetails(false)
    }
  }, [])

  const startRecord = (page - 1) * ROWS_PER_PAGE + 1
  const endRecord = Math.min(page * ROWS_PER_PAGE, total)

  return (
    <Box m="20px">
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4, mt: 1 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{
            backgroundColor: theme.palette.mode === "dark" ? colors.primary[900] : colors.primary[100],
            color: theme.palette.mode === "dark" ? colors.grey[100] : "white",
            height: "100%",
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ opacity: 0.9 }}>Total des ventes à crédit</Typography>
              <Typography variant="h3" sx={{ fontWeight: "bold" }}>{total.toLocaleString()} ventes</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" sx={{ color: theme.palette.mode === "dark" ? colors.grey[100] : "white" }}>
          Ventes par Crédit
        </Typography>

        {(user.role === "superadmin" || user.role === "supervisor") && (
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel sx={{ color: colors.grey[100] }}>Shop</InputLabel>
            <Select value={selectedShop ? selectedShop._id : "all"} onChange={handleShopChange} label="Shop">
              <MenuItem value="all">All Shops</MenuItem>
              {electronicShops.map((shop) => (
                <MenuItem key={shop._id} value={shop._id}>{shop.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)" }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            placeholder="Search by Client"
            variant="outlined"
            size="small"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            onBlur={handleSearchSubmit}
            sx={{
              flexGrow: 1, minWidth: 250,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
                "& fieldset": { borderColor: colors.grey[300] },
                "&:hover fieldset": { borderColor: colors.primary[600] },
                "&.Mui-focused fieldset": { borderColor: colors.primary[600] },
              },
            }}
          />
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker label="Start Date" value={startDate} onChange={(v) => setStartDate(v)}
              slotProps={{ textField: { size: "small", sx: { minWidth: 180 } } }} />
            <DatePicker label="End Date" value={endDate} onChange={(v) => setEndDate(v)}
              slotProps={{ textField: { size: "small", sx: { minWidth: 180 } } }} />
          </LocalizationProvider>
          <Tooltip title="Réinitialiser les filtres">
            <IconButton onClick={handleResetFilters} sx={{ color: colors.primary[500] }}><ResetIcon /></IconButton>
          </Tooltip>
        </Box>
      </Paper>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2, overflow: "auto", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
          backgroundColor: "background.paper", maxHeight: "calc(100vh - 420px)",
          "& .MuiTableRow-root": {
            "&:nth-of-type(odd)": { backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)" },
            "&:hover": { backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.04)" },
          },
          "& .MuiTableCell-root": {
            borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(81, 81, 81, 0.5)" : "rgba(224, 224, 224, 1)"}`,
            color: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.87)",
          },
          "& .MuiTableHead-root .MuiTableRow-root": {
            backgroundColor: theme.palette.mode === "dark" ? "rgb(30, 30, 30)" : "rgb(250, 250, 250)",
            "&:hover": { backgroundColor: theme.palette.mode === "dark" ? "rgb(30, 30, 30)" : "rgb(250, 250, 250)" },
            "& .MuiTableCell-root": { fontWeight: 600, backgroundColor: "transparent" },
          },
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Montant Total</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Montant payé</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Montant restant</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Effectué par</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <SkeletonRows count={10} columns={8} />
            ) : creditSales.length > 0 ? (
              creditSales.map((sale) => (
                <TableRow key={sale._id}>
                  <TableCell>{moment(sale.createdAt).format("DD/MM/YYYY HH:mm")}</TableCell>
                  <TableCell>{sale.client_name}</TableCell>
                  <TableCell sx={{ fontWeight: 500, color: colors.primary[600] }}>
                    {sale.total_amount?.toLocaleString()} Fbu
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500, color: colors.greenAccent[500] }}>
                    {sale.amount_paid?.toLocaleString()} Fbu
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500, color: colors.grey[100] }}>
                    {sale.remaining_amount?.toLocaleString()} Fbu
                  </TableCell>
                  <TableCell>{getStatusChip(sale.payment_status)}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="Payment History">
                        <IconButton onClick={() => { setSelectedSale(sale); setOpenHistory(true) }} size="small"
                          sx={{ "&:hover": { backgroundColor: colors.primary[50] } }}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {user.role !== "supervisor" && (
                        <>
                          <Tooltip title="View Details">
                            <IconButton onClick={() => { setSelectedSale(sale); setOpenProductDetails(true) }} size="small"
                              sx={{ "&:hover": { backgroundColor: colors.primary[50] } }}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Print Invoice">
                            <IconButton onClick={() => handlePrint(sale)} size="small"
                              sx={{ "&:hover": { backgroundColor: colors.primary[50] } }}>
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {sale.remaining_amount > 0 && user.role !== "supervisor" && (
                            <Tooltip title="Make Payment">
                              <IconButton
                                onClick={() => {
                                  setSelectedSale(sale)
                                  setPaymentData({ amount: sale.remaining_amount, payment_method: "cash" })
                                  setOpenPayment(true)
                                }}
                                size="small" sx={{ "&:hover": { backgroundColor: colors.primary[50] } }} color="success"
                              >
                                <PaymentIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {user.role === "superadmin" && (
                            <Tooltip title="Delete">
                              <IconButton onClick={() => handleDelete(sale._id)} size="small"
                                sx={{ "&:hover": { backgroundColor: colors.redAccent[100] } }} color="error">
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{sale.user?.name}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>No matching credit sales found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} px={1}>
        <Typography variant="body2" color="text.secondary">
          {total > 0 ? `Affichage de ${startRecord}–${endRecord} sur ${total.toLocaleString()} ventes` : "Aucune vente"}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Button size="small" startIcon={<ChevronLeft />} disabled={page <= 1 || isLoading} onClick={() => setPage(page - 1)} sx={{ textTransform: "none" }}>
            Précédent
          </Button>
          <Typography variant="body2" sx={{ mx: 1 }}>Page {page} sur {totalPages || 1}</Typography>
          <Button size="small" endIcon={<ChevronRight />} disabled={page >= totalPages || isLoading} onClick={() => setPage(page + 1)} sx={{ textTransform: "none" }}>
            Suivant
          </Button>
        </Box>
      </Box>

      <ProductDetailsDialog
        open={openProductDetails}
        onClose={() => { setOpenProductDetails(false); setSelectedSale(null) }}
        products={selectedSale?.products || []}
      />

      <Dialog
        open={openPayment}
        onClose={() => { setOpenPayment(false); setPaymentData({ amount: 0, payment_method: "cash" }) }}
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)", backgroundColor: theme.palette.background.paper },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, backgroundColor: theme.palette.mode === "dark" ? colors.primary[900] : colors.primary[100], color: colors.grey[100] }}>
          Effectuer un paiement
        </DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Montant" type="number" fullWidth value={paymentData.amount}
              onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
              sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <TextField
              label="Mode de paiement" select fullWidth value={paymentData.payment_method}
              onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
              sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            >
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="bank">Bank</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${colors.grey[300]}`, p: 2 }}>
          <Button onClick={() => setOpenPayment(false)}>Cancel</Button>
          <Button onClick={handlePayment} variant="contained" disabled={!paymentData.amount || paymentData.amount <= 0}
            sx={{ backgroundColor: colors.primary[900], color: "white" }}>
            Payer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openHistory} onClose={() => setOpenHistory(false)} maxWidth="md">
        <DialogTitle sx={{ fontWeight: 600, backgroundColor: theme.palette.mode === "dark" ? colors.primary[900] : colors.primary[100], color: colors.grey[100] }}>
          Payment History
        </DialogTitle>
        <DialogContent sx={{ py: 2, backgroundColor: theme.palette.background.paper }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: theme.palette.mode === "dark" ? colors.primary[900] : colors.primary[100] }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: colors.grey[100] }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colors.grey[100] }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colors.grey[100] }}>Method</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colors.grey[100] }}>Processed By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedSale?.payment_history?.map((payment, index) => (
                  <TableRow key={index} sx={{ "&:nth-of-type(odd)": { backgroundColor: colors.primary[900] } }}>
                    <TableCell>{moment(payment.payment_date).format("DD/MM/YYYY HH:mm")}</TableCell>
                    <TableCell>{payment.amount?.toLocaleString()} Fbu</TableCell>
                    <TableCell>{payment.payment_method === "debt" ? "Avance avec dette" : payment.payment_method}</TableCell>
                    <TableCell>{payment.user?.name || "N/A"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default VenteParCredit
